import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress,
    Alert
} from '@mui/material';
import { Print as PrintIcon, Close as CloseIcon } from '@mui/icons-material';
import { Order } from '../../../shared/types/order';
import { generateInvoice } from '../../../shared/services/orderService';
import { environment } from 'environments/environment';

interface Props {
    open: boolean;
    onClose: () => void;
    order: Order;
}

export const OrderInvoice: React.FC<Props> = ({ open, onClose, order }) => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [pdfUrl, setPdfUrl] = useState('');

    useEffect(() => {
        if (open && order) {
            fetchInvoiceBlob();
        }
    }, [open, order]);

    const fetchInvoiceBlob = async () => {
        try {
            setLoading(true);
            setError('');
            
            // Step 1: Get the invoice URL from the backend API
            const res = await generateInvoice(order.id);
            if (res.success && res.data.invoiceUrl) {
                // Step 2: Fetch the actual PDF file as a Blob using standard axios to avoid baseURL interceptors
                const { default: axios } = await import('axios');
                const pdfRes = await axios.get(res.data.invoiceUrl, { responseType: 'blob' });
                
                // Create a secure local object URL for the iframe
                const blobUrl = URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' }));
                setPdfUrl(blobUrl);
            } else {
                setError('Failed to generate invoice.');
            }
        } catch (err: any) {
            console.error('Error fetching invoice blob:', err);
            setError('Error fetching invoice PDF from the server.');
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = () => {
        if (pdfUrl) {
            const printWindow = window.open(pdfUrl, '_blank');
            if (printWindow) {
                printWindow.onload = () => {
                    printWindow.print();
                };
            }
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 1.5, borderBottom: '1px solid #e5e7eb' }}>
                <Typography variant="subtitle1" fontWeight="bold" color="text.secondary">
                    Invoice Preview
                </Typography>
                <Box display="flex" gap={1}>
                    <Button
                        variant="contained"
                        startIcon={<PrintIcon />}
                        onClick={handlePrint}
                        disabled={loading || !!error || !pdfUrl}
                        sx={{ bgcolor: '#f97316', '&:hover': { bgcolor: '#ea580c' } }}
                    >
                        Print
                    </Button>
                    <Button variant="outlined" startIcon={<CloseIcon />} onClick={onClose} color="inherit">
                        Close
                    </Button>
                </Box>
            </DialogActions>

            <DialogContent sx={{ bgcolor: '#f8fafc', p: 0, height: '75vh' }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <CircularProgress sx={{ color: '#f97316' }} />
                    </Box>
                ) : error ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%" p={3}>
                        <Alert severity="error">{error}</Alert>
                    </Box>
                ) : pdfUrl ? (
                    <iframe 
                        src={`${pdfUrl}#toolbar=0`} 
                        width="100%" 
                        height="100%" 
                        style={{ border: 'none' }}
                        title="Invoice PDF"
                    />
                ) : null}
            </DialogContent>
        </Dialog>
    );
};
